// Pure char-diff range-math extracted from hooks/index.ts.
//
// `lcsRanges` and `pairAdjacentBlocks` are the pure range-math primitives that
// drive the intra-line character-level diff highlighting in the diff viewer.
// They live here so they can be unit/property-tested without dragging the rest
// of the hooks bundle (which transitively depends on `.dox/ts/unions`) into
// the test loader.
//
// The DOM-mutating wrapper (`wrapCharRanges`) intentionally STAYS in
// hooks/index.ts — the split is range-math vs. DOM application.

export interface CharDiffResult {
  readonly delRanges: ReadonlyArray<readonly [number, number]>;
  readonly addRanges: ReadonlyArray<readonly [number, number]>;
  readonly lcs: number;
}

// Pure: classic Hunt-McIlroy LCS over chars; returns the half-open ranges in
// each side that are NOT part of the longest common subsequence, plus the LCS
// length so the caller can gate on similarity.
export const lcsRanges = (a: string, b: string): CharDiffResult => {
  const n = a.length;
  const m = b.length;
  const w = m + 1;
  const dp = new Uint16Array((n + 1) * w);
  for (let i = 1; i <= n; i++) {
    const ai = a.charCodeAt(i - 1);
    const row = i * w;
    const prev = (i - 1) * w;
    for (let j = 1; j <= m; j++) {
      if (ai === b.charCodeAt(j - 1)) {
        dp[row + j] = dp[prev + j - 1]! + 1;
      } else {
        const up = dp[prev + j]!;
        const left = dp[row + j - 1]!;
        dp[row + j] = up >= left ? up : left;
      }
    }
  }
  const aMatched = new Uint8Array(n);
  const bMatched = new Uint8Array(m);
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (a.charCodeAt(i - 1) === b.charCodeAt(j - 1)) {
      aMatched[i - 1] = 1;
      bMatched[j - 1] = 1;
      i--;
      j--;
    } else if (dp[(i - 1) * w + j]! >= dp[i * w + j - 1]!) {
      i--;
    } else {
      j--;
    }
  }
  const toRanges = (
    matched: Uint8Array,
  ): ReadonlyArray<readonly [number, number]> => {
    const ranges: Array<readonly [number, number]> = [];
    let start = -1;
    for (let k = 0; k < matched.length; k++) {
      if (matched[k] === 0) {
        if (start === -1) start = k;
      } else if (start !== -1) {
        ranges.push([start, k]);
        start = -1;
      }
    }
    if (start !== -1) ranges.push([start, matched.length]);
    return ranges;
  };
  return {
    delRanges: toRanges(aMatched),
    addRanges: toRanges(bMatched),
    lcs: dp[n * w + m]!,
  };
};

export interface CharDiffPair {
  readonly delCell: HTMLElement;
  readonly addCell: HTMLElement;
}

// Pure: walk the file's rows, group consecutive del-rows followed by
// consecutive add-rows, and pair them positionally (first del with first add,
// etc.). Uneven block sizes leave the surplus side unpaired.
export const pairAdjacentBlocks = (
  rows: ReadonlyArray<HTMLTableRowElement>,
): ReadonlyArray<CharDiffPair> => {
  const pairs: CharDiffPair[] = [];
  let i = 0;
  while (i < rows.length) {
    if (!rows[i]!.classList.contains("diff-row-del")) {
      i++;
      continue;
    }
    const dels: HTMLElement[] = [];
    while (i < rows.length && rows[i]!.classList.contains("diff-row-del")) {
      const cell = rows[i]!.querySelector<HTMLElement>(".diff-code");
      if (cell) dels.push(cell);
      i++;
    }
    const adds: HTMLElement[] = [];
    while (i < rows.length && rows[i]!.classList.contains("diff-row-add")) {
      const cell = rows[i]!.querySelector<HTMLElement>(".diff-code");
      if (cell) adds.push(cell);
      i++;
    }
    const n = Math.min(dels.length, adds.length);
    for (let k = 0; k < n; k++) {
      pairs.push({ delCell: dels[k]!, addCell: adds[k]! });
    }
  }
  return pairs;
};
